import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Menu, X } from 'lucide-react';
import { API } from '@/App';
import { EditableText } from '@/components/EditableText';

// Unified header — used on every page. Logo + 4 nav items + admin tools.
// All labels are inline-editable when logged in. Includes a full mobile menu.
export const SiteNav = ({ settings, isAdmin, setUser, updateField, updateLabel, active }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const L = settings?.labels || {};

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('session_token');
    if (token) {
      try { await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } }); } catch (e) { /* ignore */ }
    }
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    if (setUser) setUser(null);
    navigate('/');
  };

  const isActive = (key) => active === key || (active == null && location.pathname.startsWith(`/${key}`));

  const navLink = (key, route, labelKey, fallback) => {
    const isOn = isActive(key);
    const base = 'text-[11px] tracking-[0.18em] uppercase';
    const cls = isOn
      ? `${base} text-black border-b border-black pb-0.5 font-semibold`
      : `${base} text-[#525252] hover:text-black transition-colors duration-300 font-medium`;
    if (isAdmin) {
      return (
        <span className="flex items-center">
          <button onClick={() => navigate(route)} className={cls} data-testid={`nav-${key}`}>
            <EditableText
              value={L[labelKey] ?? fallback}
              onChange={(v) => updateLabel(labelKey, v)}
              editable={true}
              className={`${base} font-inherit text-current text-center min-w-[80px]`}
              testId={`nav-${key}-label`}
            />
          </button>
        </span>
      );
    }
    return (
      <button onClick={() => navigate(route)} className={cls} data-testid={`nav-${key}`}>
        {L[labelKey] || fallback}
      </button>
    );
  };

  const mobileLink = (route, label, key) => (
    <button
      onClick={() => { navigate(route); setMobileOpen(false); }}
      className={`text-left py-4 border-b border-[#E5E5E5] text-[12px] tracking-[0.2em] uppercase ${isActive(key) ? 'text-black font-semibold' : 'text-[#525252]'} hover:text-black`}
      data-testid={`mobile-nav-${key}`}
    >
      {label}
    </button>
  );

  return (
    <header className="border-b border-[#E5E5E5] bg-white relative z-30">
      <div className="px-4 sm:px-6 md:px-12 lg:px-20 py-5 sm:py-6 flex items-center justify-between gap-4">
        {isAdmin ? (
          <EditableText
            value={settings?.site_name || ''}
            onChange={(v) => updateField('site_name', v)}
            editable={true}
            className="text-xl sm:text-2xl md:text-3xl font-light tracking-tight whitespace-nowrap font-serif w-auto min-w-[180px]"
            placeholder="SITE NAME"
            testId="logo"
          />
        ) : (
          <button
            onClick={() => navigate('/work')}
            className="text-xl sm:text-2xl md:text-3xl font-light tracking-tight whitespace-nowrap hover:text-[#525252] transition-colors font-serif"
            data-testid="logo"
          >
            {settings?.site_name || 'LILY YURIKA'}
          </button>
        )}

        <nav className="hidden md:flex items-center gap-6 lg:gap-9">
          {navLink('work', '/work', 'nav_work', 'Selected Work')}
          {navLink('about', '/about', 'nav_about', 'About')}
          {navLink('contact', '/contact', 'nav_contact', 'Contact')}
          {isAdmin && (
            <>
              <span className="text-[#E5E5E5]">|</span>
              <button onClick={handleLogout} className="text-[11px] tracking-[0.18em] uppercase text-[#525252] hover:text-black font-medium" data-testid="nav-logout">
                {L.btn_logout || 'Logout'}
              </button>
            </>
          )}
          {!isAdmin && (
            <button onClick={handleLogin} className="text-[11px] tracking-[0.18em] uppercase font-semibold bg-black text-white px-5 py-2 hover:bg-[#262626] transition-colors duration-300" data-testid="nav-signin">
              {L.btn_sign_in || 'Sign In'}
            </button>
          )}
        </nav>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden p-2"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
        </button>
      </div>

      {/* Mobile menu dropdown */}
      {mobileOpen && (
        <div className="md:hidden border-t border-[#E5E5E5] bg-white" data-testid="mobile-menu">
          <nav className="flex flex-col px-4">
            {mobileLink('/work', L.nav_work || 'Selected Work', 'work')}
            {mobileLink('/about', L.nav_about || 'About', 'about')}
            {mobileLink('/contact', L.nav_contact || 'Contact', 'contact')}
            {isAdmin ? (
              <button
                onClick={() => { handleLogout(); setMobileOpen(false); }}
                className="text-left py-4 text-[12px] tracking-[0.2em] uppercase text-[#525252] hover:text-black"
                data-testid="mobile-nav-logout"
              >
                {L.btn_logout || 'Logout'}
              </button>
            ) : (
              <button
                onClick={() => { handleLogin(); setMobileOpen(false); }}
                className="my-4 bg-black text-white text-[12px] tracking-[0.2em] uppercase font-semibold py-3 text-center"
                data-testid="mobile-nav-signin"
              >
                {L.btn_sign_in || 'Sign In'}
              </button>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default SiteNav;
